import React, { ChangeEvent, useEffect, useState } from 'react'

import { ThemeProvider } from '@mui/material'
import { getTheme } from 'themes'

import type { TUpdateTaskRequestBody } from '@homepal/api-contract'
import { ETaskStatus, TSubTask } from 'types/GlobalTask'

import { useTranslation } from 'language/translator'
import { setTimestampDay } from 'utils/dateParser'

import Box from 'components_new/atoms/Box'
import Button from 'components_new/atoms/Button'
import TextField from 'components_new/atoms/TextField'

import LabelItem from 'components_new/molecules/LabelItem'
import SelectDate from 'components_new/molecules/SelectDate'
import SimpleDialog from 'components_new/molecules/SimpleDialog'

import SelectUser from 'components_new/organisms/SelectUser'
import {
  TTaskDateField,
  getTaskDateLimits
} from 'components_new/organisms/tasks/TaskDateField'
import TaskStatusChip from 'components_new/organisms/tasks/TaskStatusChip'

type TSubtaskFields = Pick<
  TSubTask,
  | 'completed_date'
  | 'deadline_date'
  | 'responsible_account_id'
  | 'started_date'
  | 'status'
  | 'title'
>

interface EditSubtaskDialogProps {
  item: TSubTask | null
  onClose: () => void
  onSubmit: (body: TUpdateTaskRequestBody) => void
}

const getFields = (item: TSubTask): TSubtaskFields => ({
  completed_date: item.completed_date,
  deadline_date: item.deadline_date,
  responsible_account_id: item.responsible_account_id,
  started_date: item.started_date,
  status: item.status,
  title: item.title
})

const EditSubtaskDialog = (props: EditSubtaskDialogProps) => {
  const { item, onClose, onSubmit } = props

  const { t } = useTranslation()

  const [fields, setFields] = useState<TSubtaskFields | null>(null)

  useEffect(() => {
    setFields(item ? getFields(item) : null)
  }, [item])

  const update = (changes: Partial<TSubtaskFields>) =>
    setFields((prev) => (prev ? { ...prev, ...changes } : prev))

  const title = fields?.title?.trim() ?? ''

  const handleSubmit = () => {
    if (!item || !fields || !title) return

    // only the dates the user changed; the backend sets the rest
    const changedDate = (field: TTaskDateField) =>
      fields[field] && fields[field] !== item[field]
        ? { [field]: fields[field] }
        : {}

    onSubmit({
      deadline_date: fields.deadline_date,
      responsible_account_id: fields.responsible_account_id,
      status: fields.status,
      title,
      ...(fields.status !== ETaskStatus.TODO
        ? changedDate('started_date')
        : {}),
      ...(fields.status === ETaskStatus.DONE
        ? changedDate('completed_date')
        : {})
    })
    onClose()
  }

  const renderDate = (field: TTaskDateField, label: string) =>
    fields ? (
      <SelectDate
        closeOnSelect={true}
        DateCalendarProps={{
          ...getTaskDateLimits(fields, field),
          onChange: (day) => {
            if (day) {
              update({
                [field]: setTimestampDay(
                  fields[field] ?? new Date().toISOString(),
                  day
                )
              })
            }
          }
        }}
        TextFieldProps={{ externalLabel: label, fullWidth: true }}
        value={fields[field]}
      />
    ) : null

  return (
    <ThemeProvider theme={getTheme('light')}>
      <SimpleDialog
        actions={
          <>
            <Button variant="text" onClick={onClose}>
              {t('global', 'cancel')}
            </Button>
            <Button
              disabled={!title}
              variant="contained"
              onClick={handleSubmit}
            >
              {t('global', 'save')}
            </Button>
          </>
        }
        onClose={onClose}
        open={Boolean(item)}
        title={t('tasks', 'edit_subtask')}
      >
        {fields ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              error={!title}
              externalLabel={t('global', 'name')}
              fullWidth={true}
              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                update({ title: event.target.value })
              }
              value={fields.title ?? ''}
            />
            <Box sx={{ display: 'flex', gap: 3 }}>
              <LabelItem label={t('global', 'status')}>
                <TaskStatusChip
                  onChange={(status) => update({ status })}
                  value={fields.status}
                />
              </LabelItem>
              <LabelItem label={t('global', 'owner')}>
                <SelectUser
                  onChange={(value) =>
                    update({ responsible_account_id: value })
                  }
                  size="small"
                  value={fields.responsible_account_id}
                />
              </LabelItem>
            </Box>
            {fields.status !== ETaskStatus.TODO
              ? renderDate('started_date', t('tasks', 'started'))
              : null}
            {fields.status === ETaskStatus.DONE
              ? renderDate('completed_date', t('tasks', 'completed'))
              : null}
            <SelectDate
              closeOnSelect={true}
              DateCalendarProps={{
                onChange: (day) => update({ deadline_date: day })
              }}
              TextFieldProps={{
                externalLabel: t('tasks', 'deadline'),
                fullWidth: true,
                placeholder: t('tasks', 'no_deadline')
              }}
              value={fields.deadline_date}
            />
          </Box>
        ) : null}
      </SimpleDialog>
    </ThemeProvider>
  )
}

export default EditSubtaskDialog
